import { useState } from "react";
import { Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { NEWS_COLORS } from "../constants/news.constants";

type NewsVideoCardProps = {
  label?: string;
  duration?: string;
  /** CDN / hosted video URL — when set, renders a real player. */
  src?: string;
  /** YouTube Video ID — when provided, renders an embedded YouTube player. */
  youtubeId?: string;
  /** Custom thumbnail / poster image path */
  poster?: string;
  /** Visual variant for placeholder thumbnails until a real video ships. */
  tone?: "city" | "event" | "national";
  className?: string;
};

const TONE_STYLES: Record<
  NonNullable<NewsVideoCardProps["tone"]>,
  { gradient: string; caption: string }
> = {
  city: {
    gradient: "from-[#1F7A5A] via-[#2D9B74] to-[#0F4C3A]",
    caption: "Delhi mobility",
  },
  event: {
    gradient: "from-[#164E3B] via-[#1F7A5A] to-[#3D8B6A]",
    caption: "Conclave highlights",
  },
  national: {
    gradient: "from-[#0F3D2E] via-[#1F7A5A] to-[#2563EB]/40",
    caption: "National address",
  },
};

/**
 * News video — real YouTube/mp4 player when provided; otherwise a styled placeholder card.
 */
export function NewsVideoCard({
  label = "Video",
  duration = "02:00",
  src,
  youtubeId,
  poster,
  tone = "city",
  className,
}: NewsVideoCardProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const style = TONE_STYLES[tone];

  // —— YouTube embed with lazy-loading interactive poster ——
  if (youtubeId) {
    if (isPlaying || !poster) {
      return (
        <div
          className={cn(
            "relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-black shadow-md",
            className
          )}
        >
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=${isPlaying ? 1 : 0}&rel=0`}
            title={label}
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="h-full w-full border-0"
          />
        </div>
      );
    }

    return (
      <div
        className={cn(
          "group relative aspect-[16/9] w-full cursor-pointer overflow-hidden rounded-2xl bg-black shadow-md",
          className
        )}
        onClick={() => setIsPlaying(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsPlaying(true);
          }
        }}
        aria-label={`Play video: ${label}`}
      >
        <img
          src={poster}
          alt={label}
          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-black/25 transition-colors group-hover:bg-black/35" />

        <span className="absolute left-3 top-3 rounded-md bg-black/65 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
          {label}
        </span>

        <div className="absolute inset-0 flex items-center justify-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-[#FF0000] text-white shadow-xl transition-transform group-hover:scale-110 sm:h-16 sm:w-16">
            <Play className="ml-1 h-6 w-6 fill-current sm:h-7 sm:w-7" aria-hidden />
          </span>
        </div>

        <span className="absolute bottom-3 right-3 rounded-full bg-black/75 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
          {duration}
        </span>
      </div>
    );
  }

  // —— Native HTML5 video player ——
  if (src) {
    return (
      <div
        className={cn(
          "relative aspect-[16/10] w-full overflow-hidden rounded-2xl bg-black shadow-md",
          className
        )}
      >
        <video
          className="h-full w-full object-cover"
          controls
          playsInline
          preload="metadata"
          aria-label={label}
        >
          <source src={src} type="video/mp4" />
        </video>
      </div>
    );
  }

  // —— Placeholder card ——
  return (
    <div
      className={cn(
        "relative aspect-[16/10] w-full overflow-hidden rounded-2xl shadow-md",
        className
      )}
      role="img"
      aria-label={`${label} placeholder — ${style.caption}`}
    >
      <div
        className={cn(
          "absolute inset-0 bg-gradient-to-br",
          style.gradient
        )}
      />
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.35), transparent 45%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.2), transparent 40%)",
        }}
      />

      <span className="absolute left-3 top-3 rounded-md bg-black/55 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-white backdrop-blur-sm">
        {label}
      </span>

      <div className="absolute inset-0 flex items-center justify-center">
        <span
          className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 shadow-lg sm:h-16 sm:w-16"
          style={{ color: NEWS_COLORS.primary }}
        >
          <Play className="h-6 w-6 fill-current sm:h-7 sm:w-7" aria-hidden />
        </span>
      </div>

      <span className="absolute bottom-3 right-3 rounded-full bg-black/70 px-2.5 py-1 text-xs font-medium text-white">
        {duration}
      </span>
    </div>
  );
}
